import { defineStore } from 'pinia';
import { ref } from 'vue';
import { fetchUnreadCount } from '@/api/chat';
import { useAuthStore } from '@/stores/auth.store';
import { AUTH_TOKEN_KEY } from '@/api/client';

/**
 * Compteur de messages chat non lus -- alimente le badge sur les icones
 * Chat (HeaderGlobalNav desktop + MobileBottomNav drawer).
 *
 * Poll toutes les 45s. Plus rapide que l'inbox parce que le chat est
 * un canal vivant (discussions courtes, on attend une reponse). Le
 * ChatView appelle refresh() apres markRoomAsRead pour decrement
 * immediat. SignalR pourrait aussi pousser des refresh si on branche
 * l'event en push.
 */
const POLL_INTERVAL_MS = 45_000;

export const useChatUnreadStore = defineStore('chat-unread', () => {
  const unreadCount = ref(0);
  const loading = ref(false);
  let inFlight: Promise<void> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const auth = useAuthStore();

  async function refresh(): Promise<void> {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      unreadCount.value = 0;
      return;
    }
    if (!auth.user) return;
    if (inFlight) return inFlight;
    loading.value = true;
    inFlight = (async () => {
      try {
        unreadCount.value = await fetchUnreadCount();
      } catch {
        // Silencieux : un fail de count ne doit pas spammer l'utilisateur.
      } finally {
        loading.value = false;
        inFlight = null;
      }
    })();
    return inFlight;
  }

  function start(): void {
    if (pollTimer) return;
    void refresh();
    pollTimer = setInterval(refresh, POLL_INTERVAL_MS);
  }

  function stop(): void {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function clear(): void {
    unreadCount.value = 0;
  }

  return { unreadCount, loading, refresh, start, stop, clear };
});
